<template>
  <div class="soar-container">
    <FilevaliditySkeleton v-if="isLoading"></FilevaliditySkeleton>
    <template v-else>
      <div class="w-full my-6 flex flex-col">
        <div class="border-b border-brand-gray-350 py-3">
          <h2 class="text-xl text-gray-500 font-noraml mx-3">
            {{ $t('sponsoredManagement') }}
          </h2>
        </div>
        <div class="flex items-center px-6 py-6">
          <div class="w-full flex gap-x-4 gap-y-1 text-brand-black-secondary">
            <InfoBar>
              <span class="flex-1 text-justify my-3 text-white text-base h-full w-full">
                {{ $t('sponsorDescription') }}
              </span>
            </InfoBar>
          </div>
        </div>
        <div class="w-full">
          <Form
            :initial-values="initialValues"
            :keep-values="true"
            class="flex flex-col gap-y-5"
            @submit="(values) => searchEmployee(values)"
            v-slot="{ values }"
          >
            <fieldset class="border-2 border-primary rounded p-4 flex flex-col gap-y-5 mx-4">
              <legend class="px-4 text-xl">{{ $t('search') }}</legend>
              <div class="flex flex-col gap-6">
                <div class="flex items-center gap-x-8">
                  <Field name="unifiedNumber" v-slot="{ field, errors: errs }">
                    <SimpleTextInput
                      :field="field"
                      :errors="errs"
                      :label="$t('unifiedNumber')"
                      :maxLength="10"
                      direction="flex-col"
                      :isRequired="false"
                      class="md:w-1/2 flex-col"
                      :isDisabled="values?.identityNumber"
                    />
                  </Field>

                  <Field name="identityNumber" v-slot="{ field, errors: errs }">
                    <SimpleTextInput
                      :field="field"
                      :errors="errs"
                      :label="$t('identityNumber')"
                      :maxLength="10"
                      direction="flex-col"
                      :isRequired="false"
                      class="md:w-1/2 flex-col"
                      :isDisabled="values?.unifiedNumber"
                    />
                  </Field>
                </div>

                <div class="w-full flex items-center justify-center gap-x-8">
                  <div class="md:w-1/2 flex gap-4">
                    <DatepickerCalendar
                      :show-error-spacer="false"
                      :label="$t('fromDate')"
                      label-class="w-full"
                      direction="flex-col justify-start"
                      field-name="fromDate"
                      class="w-full"
                      :isDisabled="values?.unifiedNumber || values?.identityNumber"
                    >
                    </DatepickerCalendar>

                    <DatepickerCalendar
                      :show-error-spacer="false"
                      :label="$t('toDate')"
                      label-class="w-full"
                      direction="flex-col justify-start"
                      field-name="toDate"
                      class="w-full"
                      :isDisabled="values?.unifiedNumber || values?.identityNumber"
                    >
                    </DatepickerCalendar>
                  </div>

                  <Field name="searchCriteriaName" v-slot="{ field, errors: errs }">
                    <SimpleTextInput
                      :field="field"
                      :errors="errs"
                      :label="$t('name')"
                      :maxLength="10"
                      direction="flex-col"
                      class="md:w-1/2 flex-col"
                      :isDisabled="values?.unifiedNumber || values?.identityNumber"
                    />
                  </Field>
                </div>
                <div class="w-full flex items-center gap-8">
                  <Field name="nationality" v-slot="{ field, errors: errs, handleChange }">
                    <SimpleDropdown
                      :field="field"
                      :errors="errs"
                      :label="$t('nationality')"
                      :options="countries"
                      :is-required="false"
                      label-class="text-xs"
                      class="w-full text-xs flex-wrap"
                      :disabled="values?.unifiedNumber || values?.identityNumber"
                      @on-selection="(item) => handleChange(+item?.code)"
                    ></SimpleDropdown>
                  </Field>
                  <Field
                    name="selectedModule"
                    v-slot="{ field, errors: errs, handleChange }"
                    v-if="showModules"
                  >
                    <SimpleDropdown
                      :field="field"
                      :errors="errs"
                      :label="$t('selectedModule')"
                      :options="countries"
                      :is-required="false"
                      label-class="text-xs"
                      class="w-full text-xs flex-wrap"
                      @on-selection="(item) => handleChange(+item?.code)"
                    ></SimpleDropdown>
                  </Field>
                </div>
                <div class="w-full md:w-1/2 pr-4" v-if="showModules">
                  <Field name="serviceStatusId" v-slot="{ field, errors: errs, handleChange }">
                    <SimpleDropdown
                      :field="field"
                      :errors="errs"
                      :label="$t('serviceStatusId')"
                      :options="countries"
                      :is-required="false"
                      label-class="text-xs"
                      class="w-full text-xs flex-wrap"
                      @on-selection="(item) => handleChange(+item?.code)"
                    ></SimpleDropdown>
                  </Field>
                </div>
                <div class="w-full md:w-1/2 flex item-center gap-4" v-if="showModules">
                  <Field
                    :value="true"
                    :unchecked-value="false"
                    v-slot="{ field }"
                    name="isViolatorsFlag"
                    type="checkbox"
                  >
                    <label>
                      <input
                        class="accent-primary"
                        type="checkbox"
                        name="violatorsRequests"
                        v-bind="field"
                      />
                      {{ $t('violatorsRequests') }}
                    </label>
                  </Field>

                  <Field
                    :value="true"
                    :unchecked-value="false"
                    v-slot="{ field }"
                    name="isActiveLeavePermitFlag"
                    type="checkbox"
                  >
                    <label>
                      <input
                        class="accent-primary"
                        type="checkbox"
                        name="activeLeavePermits"
                        v-bind="field"
                      />
                      {{ $t('activeLeavePermits') }}
                    </label>
                  </Field>
                </div>

                <div
                  class="w-full md:w-1/2 pr-4"
                  v-if="client_config.enableSponsoredOrderByOptions"
                >
                  <Field
                    name="orderByColumn"
                    v-slot="{ field, errors: errs, handleChange }"
                    v-if="showModules"
                  >
                    <SimpleDropdown
                      :field="field"
                      :errors="errs"
                      :label="$t('orderByColumn')"
                      :options="countries"
                      :is-required="false"
                      label-class="text-xs"
                      class="w-full text-xs flex-wrap"
                      @on-selection="(item) => handleChange(+item?.code)"
                    ></SimpleDropdown>
                  </Field>
                </div>

                <div class="flex justify-end items-center">
                  <FilledButton class="px-5 py-1.5 text-white">
                    {{ $t('search') }}
                  </FilledButton>
                </div>
              </div>
            </fieldset>
          </Form>
        </div>
        <div class="border-b w-full border-brand-gray-300 py-4 px-4"></div>
        <div class="w-full"></div>
      </div>
    </template>
  </div>
</template>

<script setup>
import InfoBar from '@/components/Form/InfoBar.vue'
import FilledButton from '@/components/Buttons/Filled.vue'
import SimpleDropdown from '@/components/SimpleForm/Dropdown.vue'
import SimpleTextInput from '@/components/SimpleForm/TextInput.vue'
import FilevaliditySkeleton from '@/views/file-validity/skeleton.vue'
import { useLookupsStore } from '@/stores/lookups'
import DatepickerCalendar from '@/components/Form/DatepickerCalendar.vue'
import { Field, Form } from 'vee-validate'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import apiHelperService from '@/services'
import { useFormStore } from '@/stores/form'
import { useRoute } from 'vue-router'
import client_config from '@/assets/json/client_config.json'
import lookups from '@/assets/json/clientLookups.json'

const route = useRoute()
const lookupsStore = useLookupsStore()
const formStore = useFormStore()

const isLoading = ref(false)
const modules = ref([])
const parentService = ref([])
const service = ref([])
const transaction = ref([])
const countries = ref([])
const serviceStatus = ref([])
const specSponsoredOrderBy = ref([])
const filterdModule = ref([])
const currentLanguage = ref('en')
const selectedModule = ref()
const employeeCollection = ref([])


const searchEmployee = async (value) => {
  try {
    const payload = value
    const response = await apiHelperService.post('/api/individual/sponsored', payload)
    employeeCollection.value = response.collection
  } catch (error) {
    console.error('searchEmployee', error)
  }
}

const showModules = computed(() => {
  if (!route.query?.transactionId) return true
})

const loadModules = async () => {
  modules.value = await lookupsStore.fetchLookup('/api/lookup/modules')
  if (route.query.moduleId) {
    selectedModule.value = parseInt(route.query.moduleId)
  }
  filterdModule.value = modules.value.filter(
    (f) => f.id === lookups.module.residency || f.id === lookups.module.visa
  )
}

const loadServices = async () => {
  service.value = await lookupsStore.fetchLookup('/api/lookup/services')
}

const loadModuleParentService = async () => {
  parentService.value = await lookupsStore.fetchLookup('/api/lookup/moduleParentService')
}

const loadTransactions = async () => {
  transaction.value = await lookupsStore.fetchLookup('/api/lookup/transactions')
}

const loadCountries = async () => {
  countries.value = await lookupsStore.fetchLookup('/api/lookup/countries')
}
const loadFileStatus = async () => {
  serviceStatus.value = await lookupsStore.fetchLookup('/api/lookup/fileStatus')
}

const loadSortableColumns = async () => {
  specSponsoredOrderBy.value = await lookupsStore.fetchLookup('/api/lookup/sortableColumns')
  specSponsoredOrderBy.value = specSponsoredOrderBy.value.map((s) => {
    s.text = currentLanguage.value === 'ar' ? s.arName : s.enName
    return s
  })
}

onMounted(async () => {
  await loadModules()
  await loadServices()
  await loadModuleParentService()
  await loadTransactions()
  await loadCountries()
  await loadFileStatus()
  await loadSortableColumns()
})

onBeforeUnmount(() => {
  formStore.$reset()
})
</script>

<style></style>
